import { CompassUserContext } from '@orientaction/Context';
import { useAlert, useEbook } from '@orientaction/hooks';
import { appLanguage } from '@orientaction/reducers';
import {
  getFromLS,
  RATTACHED_BENEFICIARY_SERVICE_IDS,
  userHasAnyService,
  userHasService,
} from '@orientaction/utils';
import { concat } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiServiceNative } from '../../../../utils/fetchNative';

const qs = require('qs');

const journalPopulate = {
  populate: {
    service_assignments: {
      populate: {
        service: { fields: ['id', 'name'] },
        games: { fields: ['id'] },
        ebooks: { fields: ['id'] },
        book_templates: { fields: ['id'] },
      },
    },
    service: { fields: ['id', 'name'] },
    services: { fields: ['id', 'name'] },
  },
};

const readJournalUser = (response: any) => response?.data?.data || response?.data || response;

const fetchConnectedUserJournal = async (userId?: number) => {
  const params = qs.stringify(journalPopulate, { encodeValuesOnly: true });
  let user: any;

  try {
    user = readJournalUser(await apiServiceNative.get(`/api/users/me?${params}`));
  } catch {
    user = null;
  }

  if ((!user?.service_assignments || !user?.service_resources_map) && userId) {
    try {
      const byId = readJournalUser(await apiServiceNative.get(`/api/users/${userId}?${params}`));
      user = {
        ...user,
        ...byId,
        service_assignments: byId?.service_assignments || user?.service_assignments,
        service_resources_map: byId?.service_resources_map || user?.service_resources_map,
      };
    } catch {
      // Keep the /me payload when the user endpoint is not allowed.
    }
  }

  return {
    assignments: user?.service_assignments,
    resourcesMap: user?.service_resources_map,
  };
};

export const useAccessCheck = () => {
  const { userInfos, loading } = useContext<any>(CompassUserContext);
  const [loadingResources, setLoadingResources] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  useEffect(() => {
    const getListGame = async () => {
      setLoadingResources(true);
      const response = await apiServiceNative.get('/api/game-list-user');
      const responseEbook = await apiServiceNative.get('/api/ebook-list-user');
      const check =
        (userInfos?.rattachement &&
          userHasAnyService(userInfos, RATTACHED_BENEFICIARY_SERVICE_IDS)) ||
        (response && response.length > 0) ||
        (responseEbook && responseEbook.length > 0);
      setCanAccess(check);
      setLoadingResources(false);
    };

    if (userInfos) {
      getListGame();
    }
  }, [userInfos]);

  return { userInfos, loading: loading || loadingResources, canAccess };
};

export const useAttachedResources = () => {
  const alert = useAlert();
  const { getMyBookRequest } = useEbook({ mode: 'none', user: 'none' });
  const lang: string = useSelector(appLanguage);
  const currentUser = JSON.parse(getFromLS('userInfos') || '{}');
  const { userInfos, loading: userLoading } = useContext<any>(CompassUserContext);

  const [allBook, setAllBook] = useState<any[]>([]);
  const [list, setList] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any>(undefined);
  const [resourcesMap, setResourcesMap] = useState<any>(undefined);
  const [assignmentsReady, setAssignmentsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingEbooks, setLoadingEbooks] = useState(false);

  const getListEbookSerious = async () => {
    setLoadingEbooks(true);
    const response = await apiServiceNative.get('/api/ebook-list-user');
    setLoadingEbooks(false);
    return response;
  };

  const getEbookRecrutement = async () => {
    try {
      const response = await apiServiceNative.get(
        `/api/job/ebook-recrutement-user/${currentUser.id}`,
      );
      return response.data;
    } catch (err) {
      return undefined;
    }
  };

  const getListGame = async () => {
    setLoading(true);
    const response = await apiServiceNative.get('/api/game-list-user');
    setList(response);
    setLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setAllBook([]);

      try {
        const reponse = await getMyBookRequest('', 0);
        const responseEbookSeious = await getListEbookSerious();
        const responseEbookRecrutement = await getEbookRecrutement();
        const { data } = await apiServiceNative.get(
          `/api/job/find-check-candidature/${currentUser.id}`,
        );

        if (reponse?.data?.list.length > 0) {
          localStorage.setItem('book', reponse?.data?.list[0]?.id);
        }

        const oldEbook =
          userHasAnyService(userInfos, RATTACHED_BENEFICIARY_SERVICE_IDS) &&
          userInfos?.rattachement
            ? reponse?.data?.list.sort((a: any, b: any) => a.attributes.id - b.attributes.id)
            : [];

        const newEbook = responseEbookSeious.map((elem: any) => {
          elem.isNew = true;
          return elem;
        });

        if (responseEbookRecrutement) {
          responseEbookRecrutement.isRecrutement = true;
        }

        const concatEbook =
          !responseEbookRecrutement || !data
            ? userHasService(userInfos, 6)
              ? concat(oldEbook, responseEbookRecrutement, newEbook)
              : concat(oldEbook, newEbook)
            : concat(oldEbook, responseEbookRecrutement, newEbook);

        setAllBook(concatEbook);
      } catch (error) {
        alert.openSimpleAlert();
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh resources when locale changes
  }, [lang]);

  useEffect(() => {
    getListGame();
  }, []);

  const connectedUserId = Number(userInfos?.id || currentUser?.id) || 0;

  useEffect(() => {
    if (!connectedUserId) {
      setAssignments(userInfos?.service_assignments);
      setResourcesMap(userInfos?.service_resources_map);
      setAssignmentsReady(true);
      return undefined;
    }

    let cancelled = false;
    const loadAssignments = async () => {
      try {
        const journal = await fetchConnectedUserJournal(connectedUserId);
        if (!cancelled) {
          setAssignments(journal.assignments ?? userInfos?.service_assignments);
          setResourcesMap(journal.resourcesMap ?? userInfos?.service_resources_map);
        }
      } catch {
        if (!cancelled) {
          setAssignments(userInfos?.service_assignments);
          setResourcesMap(userInfos?.service_resources_map);
        }
      } finally {
        if (!cancelled) {
          setAssignmentsReady(true);
        }
      }
    };

    loadAssignments();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- journal is loaded once the connected user id is known
  }, [connectedUserId]);

  return {
    allBook,
    list,
    assignments,
    resourcesMap,
    userInfos,
    loading: loading || userLoading || loadingEbooks || !assignmentsReady,
  };
};
